import { useEffect, useRef, useState } from "react";
import { motion, useScroll, useTransform, AnimatePresence } from "framer-motion";
import { Play, Lock } from "lucide-react";
import Reveal from "./Reveal";

const VIMEO_SRC =
    "https://player.vimeo.com/video/1194927028?badge=0&autopause=0&autoplay=1&muted=1&background=0&playsinline=1&controls=0&player_id=vframe&app_id=58479";

const EASE = [0.16, 1, 0.3, 1];

function MaskedLine({ children, index }) {
    return (
        <span className="-mb-[0.1em] block overflow-hidden pb-[0.1em]">
            <motion.span
                className="block"
                initial={{ y: "115%" }}
                animate={{ y: "0%" }}
                transition={{ delay: 0.3 + index * 0.13, duration: 1, ease: EASE }}
            >
                {children}
            </motion.span>
        </span>
    );
}

function VideoPlayer() {
    const iframeRef = useRef(null);
    const playerRef = useRef(null);
    const [started, setStarted] = useState(false);
    const [pausado, setPausado] = useState(false);
    const [terminado, setTerminado] = useState(false);
    const [rate, setRate] = useState(1);
    const [gate, setGate] = useState({ vistos: 0, abierto: false });
    const viewers = 318;

    useEffect(() => {
        const t = setInterval(() => {
            setGate({ vistos: window.__pgVistos || 0, abierto: !!window.__pgAbierto });
        }, 1000);
        return () => clearInterval(t);
    }, []);

    useEffect(() => {
        const init = () => {
            if (window.Vimeo && iframeRef.current && !playerRef.current) {
                playerRef.current = new window.Vimeo.Player(iframeRef.current);
                playerRef.current.on("pause", () => setPausado(true));
                playerRef.current.on("play", () => {
                    setPausado(false);
                    setTerminado(false);
                });
                playerRef.current.on("ended", () => setTerminado(true));
            }
        };
        init();
        window.addEventListener("load", init);
        return () => window.removeEventListener("load", init);
    }, []);

    const handlePlay = () => {
        const p = playerRef.current;
        if (p) {
            p.setVolume(1);
            p.setMuted(false);
            p.setCurrentTime(0);
            p.play();
        }
        setStarted(true);
    };

    const handleResume = () => {
        const p = playerRef.current;
        if (!p) return;
        if (terminado) p.setCurrentTime(0);
        p.play();
    };

    const restan = Math.max(0, 120 - Math.floor(gate.vistos));
    const tmm = Math.floor(restan / 60);
    const tss = String(restan % 60).padStart(2, "0");

    return (
        <div>
            <div className="relative aspect-video overflow-hidden rounded-2xl border border-[#222] bg-black shadow-[0_30px_80px_-30px_rgba(255,92,0,0.25)]">
            <iframe
                ref={iframeRef}
                id="vframe"
                src={VIMEO_SRC}
                className="absolute inset-0 h-full w-full"
                frameBorder="0"
                allow="autoplay; fullscreen; picture-in-picture"
                allowFullScreen
                playsInline
                title="Video de presentación"
                data-testid="vsl-iframe"
            />
            <AnimatePresence>
                {!started && (
                    <motion.button
                        key="facade"
                        onClick={handlePlay}
                        exit={{ opacity: 0, scale: 1.05 }}
                        transition={{ duration: 0.5, ease: EASE }}
                        className="absolute inset-0 flex cursor-pointer flex-col items-center justify-center bg-gradient-to-b from-[#0d0d0d]/60 via-[#0d0d0d]/75 to-[#0d0d0d]/92 backdrop-blur-[5px]"
                        data-testid="vsl-play-facade"
                        aria-label="Reproducir video con sonido"
                    >
                        <div className="absolute left-3 top-3 flex items-center gap-2 rounded-full border border-[#333] bg-[#0d0d0d]/80 px-3 py-1.5 backdrop-blur-md sm:left-4 sm:top-4">
                            <span className="dot-blink h-2 w-2 rounded-full bg-[#22c55e]" />
                            <span className="text-[11px] font-medium text-[#c9c9c9] sm:text-xs" data-testid="viewer-counter">
                                {viewers} personas vieron esto hoy
                            </span>
                        </div>
                        <span className="relative flex h-20 w-20 items-center justify-center sm:h-24 sm:w-24">
                            <span className="pulse-ring absolute inset-0 rounded-full border border-[#ff5c00]/60" />
                            <span className="pulse-ring absolute inset-0 rounded-full border border-[#ff5c00]/40" style={{ animationDelay: "0.6s" }} />
                            <span className="flex h-16 w-16 items-center justify-center rounded-full bg-gradient-to-br from-[#ff5c00] to-[#ff7a30] shadow-[0_0_50px_rgba(255,92,0,0.55)] transition-transform duration-300 hover:scale-105 sm:h-[4.5rem] sm:w-[4.5rem]">
                                <Play className="ml-1 h-7 w-7 fill-[#0d0d0d] text-[#0d0d0d]" />
                            </span>
                        </span>
                        <span className="serif-accent mt-5 text-lg text-[#d8d8d8] sm:text-xl">toca para verlo con sonido</span>
                    </motion.button>
                )}
            </AnimatePresence>
            {started && pausado && (
                <button
                    onClick={handleResume}
                    className="absolute inset-0 z-10 flex cursor-pointer items-center justify-center bg-[#0d0d0d]/35"
                    aria-label="Continuar el video"
                    data-testid="vsl-resume-overlay"
                >
                    <span className="flex items-center gap-2.5 rounded-full border border-[#ff5c00]/50 bg-[#0d0d0d]/85 px-5 py-2.5 shadow-[0_0_40px_rgba(255,92,0,0.35)] backdrop-blur-md">
                        <Play className="h-4 w-4 fill-[#ff5c00] text-[#ff5c00]" />
                        <span className="text-xs font-bold text-[#f5f5f5] sm:text-sm">
                            {terminado ? "Ver de nuevo" : "Toca para continuar"}
                        </span>
                    </span>
                </button>
            )}
            {started && !pausado && (
                <div
                    className="absolute bottom-3 right-3 z-10 flex gap-1 rounded-full border border-[#333] bg-[#0d0d0d]/85 p-1 backdrop-blur-md"
                    data-testid="speed-control"
                >
                    {[1, 1.5, 2].map((r) => (
                        <button
                            key={r}
                            onClick={() => {
                                if (playerRef.current) playerRef.current.setPlaybackRate(r);
                                setRate(r);
                            }}
                            className={`rounded-full px-2.5 py-1 text-[11px] font-bold transition-colors ${
                                rate === r
                                    ? "bg-gradient-to-r from-[#ff5c00] to-[#ff7a30] text-[#0d0d0d]"
                                    : "text-[#888] hover:text-[#f5f5f5]"
                            }`}
                            aria-label={`Ver a velocidad ${r}x`}
                            data-testid={`speed-${r}x`}
                        >
                            {r}x
                        </button>
                    ))}
                </div>
            )}
            </div>
            <div className="mt-5">
                {gate.abierto ? (
                    <button
                        onClick={() => window.scrollToCalendar()}
                        className="btn-primary w-full py-4 text-sm sm:text-base"
                        data-testid="hero-agenda-button"
                    >
                        Agendar mi consultoría — Gratis
                    </button>
                ) : (
                    <div>
                        <button
                            disabled
                            aria-disabled="true"
                            className="flex w-full cursor-not-allowed items-center justify-center gap-2.5 rounded-full border border-[#2a2a2a] bg-[#111111] px-4 py-4 text-sm font-bold text-[#666] sm:gap-3 sm:text-base"
                            data-testid="hero-agenda-button"
                        >
                            <Lock className="h-4 w-4 shrink-0" />
                            <span className="whitespace-nowrap">Agendar mi consultoría</span>
                            <span
                                className="flex items-center gap-1.5 rounded-full border border-[#333] bg-[#0d0d0d] px-2.5 py-1 text-xs font-bold text-[#ff7a30] [font-variant-numeric:tabular-nums]"
                                data-testid="agenda-timer"
                            >
                                <span className="dot-blink h-1.5 w-1.5 rounded-full bg-[#ff5c00]" />
                                {tmm}:{tss}
                            </span>
                        </button>
                        <p className="mt-2.5 text-center text-xs text-[#777]">
                            Se desbloquea al ver 2 minutos del video
                        </p>
                    </div>
                )}
            </div>
        </div>
    );
}

const STEPS = ["Mira el video", "Agenda tu consultoría", "Empieza a generar"];

export default function Hero() {
    const sectionRef = useRef(null);
    const { scrollYProgress } = useScroll({ target: sectionRef, offset: ["start start", "end start"] });
    const glowY = useTransform(scrollYProgress, [0, 1], [0, 150]);
    const videoY = useTransform(scrollYProgress, [0, 1], [0, 60]);

    return (
        <section ref={sectionRef} className="relative overflow-hidden px-5 pb-16 pt-24 sm:pb-24 sm:pt-32" data-testid="hero-section">
            <motion.div
                style={{ y: glowY }}
                className="pointer-events-none absolute -top-32 left-1/2 h-[420px] w-[720px] -translate-x-1/2 rounded-full bg-[#ff5c00]/[0.13] blur-[130px]"
            />
            <div className="pointer-events-none absolute -left-40 top-1/3 h-72 w-72 rounded-full bg-[#ff5c00]/[0.06] blur-[100px]" />

            <div className="relative mx-auto max-w-4xl text-center">
                <motion.div
                    initial={{ opacity: 0, y: 16 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.8, ease: EASE }}
                    className="mb-6 inline-flex items-center gap-2 rounded-full border border-[#2a2a2a] bg-[#111111]/80 px-4 py-1.5"
                >
                    <span className="dot-blink h-1.5 w-1.5 rounded-full bg-[#ff5c00]" />
                    <span className="text-[11px] font-medium uppercase tracking-[0.18em] text-[#999] sm:text-xs">
                        Solo para los que van en serio
                    </span>
                </motion.div>

                <h1 className="font-display text-4xl font-extrabold leading-[1.04] sm:text-5xl lg:text-6xl" data-testid="hero-headline">
                    <MaskedLine index={0}>
                        Así generan <em className="serif-accent text-grad">dominicanos</em>
                    </MaskedLine>
                    <MaskedLine index={1}>
                        <span className="text-grad">$1,000</span> a <span className="text-grad">$5,000 USD</span>
                    </MaskedLine>
                    <MaskedLine index={2}>al mes vendiendo online</MaskedLine>
                    <MaskedLine index={3}>
                        desde <em className="serif-accent">casa</em>.
                    </MaskedLine>
                </h1>

                <motion.p
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.85, duration: 0.9, ease: EASE }}
                    className="mx-auto mt-6 max-w-xl text-sm leading-relaxed text-[#999] sm:text-base"
                    data-testid="hero-subtitle"
                >
                    El modelo exacto con el que opero desde RD. Mira el video completo y agenda tu consultoría gratuita con el equipo.
                </motion.p>

                <motion.div
                    style={{ y: videoY }}
                    initial={{ opacity: 0, y: 40, scale: 0.97 }}
                    animate={{ opacity: 1, y: 0, scale: 1 }}
                    transition={{ delay: 1, duration: 1, ease: EASE }}
                    className="mt-10"
                >
                    <VideoPlayer />
                </motion.div>

                <Reveal delay={150} className="mt-10">
                    <div className="mx-auto grid max-w-2xl grid-cols-1 gap-3 sm:grid-cols-3 sm:gap-0 sm:divide-x sm:divide-[#222]" data-testid="hero-steps">
                        {STEPS.map((step, i) => (
                            <div key={step} className="flex items-center justify-center gap-3 px-4 py-2" data-testid={`hero-step-${i + 1}`}>
                                <span className="font-display text-2xl font-extrabold text-grad">{i + 1}</span>
                                <span className="text-sm text-[#bbb]">{step}</span>
                            </div>
                        ))}
                    </div>
                </Reveal>
            </div>
        </section>
    );
}
